/**
 * Who a receiving screen, query, draft or pending request belongs to (batch 8 T12c2, #3033).
 *
 * Everything the receiving screens hold (cached history, drafts, a reviewed summary, a request whose outcome is unknown) belongs
 * to ONE signed-in session of one user in one tenant, with the permissions it holds now, working on ONE purchase order:
 *
 *  - `owner` = tenant + user + `sessionFamilyId`: the persistent identity of a pending request. It survives a reload and a token
 *    rotation, and does not survive a new login, a logout or another tenant or user. A session restored from before families
 *    existed is the single legacy session of that user.
 *  - `epoch` (`sessionEpoch`) is the in-memory generation. It is not persisted; it fences work in flight in this page.
 *  - permissions are read as primitives from the store (never the stable `hasPermission` function, whose identity does not change
 *    when the permissions do), so a downgrade re-renders in the same pass, and they are read again at every dispatch and return.
 *  - a locked portal is not active: nothing is read or sent while the lock overlay is up.
 *  - a `Fence` is taken at dispatch and watches the store until the answer is back. It breaks for good on ANY change of owner,
 *    generation, lock or of the three permissions, so losing and regaining a permission (or A, then B, then A) while a request is
 *    in flight never makes the answer current again.
 */

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

export const RECEIPT_READ_PERMISSION = 'inventory.read';
export const RECEIPT_WRITE_PERMISSION = 'purchase_orders.receive';
/** Cost-bearing details of a receipt (order line price, discount, cost layers) exclusively. */
export const RECEIPT_FINANCIAL_PERMISSION = 'reports.view_financial';

export const LEGACY_FAMILY = 'legacy-session';

export interface ReceivingOwner {
  readonly tenantId: string;
  readonly userId: string;
  readonly sessionFamilyId: string;
}

export interface ReceivingAccess {
  /** Identity of the signed-in session (also while locked). `null`: nobody is signed in. */
  readonly owner: ReceivingOwner | null;
  readonly epoch: number;
  /** Signed in and not locked: the only state in which anything may be read or sent. */
  readonly active: boolean;
  /** `inventory.read`: the receipt history. */
  readonly canRead: boolean;
  /** `purchase_orders.receive`: receive, decide held units, close short. */
  readonly canReceive: boolean;
  /** `reports.view_financial`: costs, exclusively. */
  readonly canFinancial: boolean;
}

export const ownerEquals = (a: ReceivingOwner | null, b: ReceivingOwner | null): boolean =>
  a !== null && b !== null && a.tenantId === b.tenantId && a.userId === b.userId && a.sessionFamilyId === b.sessionFamilyId;

/** A string that is equal exactly for equal owners. */
export const ownerId = (owner: ReceivingOwner): string => JSON.stringify([owner.tenantId, owner.userId, owner.sessionFamilyId]);

type AuthSnapshot = ReturnType<typeof useAuthStore.getState>;

function buildOwner(tenantId: unknown, userId: unknown, family: unknown, authenticated: boolean): ReceivingOwner | null {
  if (!authenticated) return null;
  if (typeof tenantId !== 'string' || tenantId === '' || typeof userId !== 'string' || userId === '') return null;
  return { tenantId, userId, sessionFamilyId: typeof family === 'string' && family !== '' ? family : LEGACY_FAMILY };
}

function buildAccess(
  owner: ReceivingOwner | null,
  epoch: number,
  locked: boolean,
  holds: { read: boolean; receive: boolean; financial: boolean },
): ReceivingAccess {
  const active = owner !== null && !locked;
  return {
    owner,
    epoch,
    active,
    canRead: active && holds.read,
    canReceive: active && holds.receive,
    canFinancial: active && holds.financial,
  };
}

/** The access of the store as it is at this moment. Use it at the moment of an action, never a captured copy. */
export function readReceivingAccess(state: AuthSnapshot = useAuthStore.getState()): ReceivingAccess {
  return buildAccess(buildOwner(state.user?.tenantId, state.user?.id, state.sessionFamilyId, state.isAuthenticated), state.sessionEpoch, state.isLocked, {
    read: state.hasPermission(RECEIPT_READ_PERMISSION),
    receive: state.hasPermission(RECEIPT_WRITE_PERMISSION),
    financial: state.hasPermission(RECEIPT_FINANCIAL_PERMISSION),
  });
}

/** The reactive access. Every input is a primitive, so an owner or permission change re-renders in the same pass. */
export function useReceivingAccess(): ReceivingAccess {
  const tenantId = useAuthStore((state) => state.user?.tenantId ?? null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const family = useAuthStore((state) => state.sessionFamilyId);
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const locked = useAuthStore((state) => state.isLocked);
  const epoch = useAuthStore((state) => state.sessionEpoch);
  const read = useAuthStore((state) => state.hasPermission(RECEIPT_READ_PERMISSION));
  const receive = useAuthStore((state) => state.hasPermission(RECEIPT_WRITE_PERMISSION));
  const financial = useAuthStore((state) => state.hasPermission(RECEIPT_FINANCIAL_PERMISSION));
  return useMemo(
    () => buildAccess(buildOwner(tenantId, userId, family, authenticated), epoch, locked, { read, receive, financial }),
    [tenantId, userId, family, authenticated, locked, epoch, read, receive, financial],
  );
}

/**
 * Identity of everything one signed-in owner may see and do: owner, generation and rights. Used as a React `key`, so a different
 * owner, a new login of the same user or any change of rights remounts the subtree in the same render that sees it.
 */
export function accessKey(access: ReceivingAccess): string {
  if (access.owner === null || !access.active) return 'none';
  return JSON.stringify([
    access.owner.tenantId,
    access.owner.userId,
    access.owner.sessionFamilyId,
    access.epoch,
    access.canRead,
    access.canReceive,
    access.canFinancial,
  ]);
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/** Refused before any request: the caller lacks what the endpoint needs. */
export class ReceivingAccessError extends Error {
  constructor(readonly required: readonly string[]) {
    super(`Missing permission: ${required.join(', ')}`);
    this.name = 'ReceivingAccessError';
  }
}

/** Refused before any request: the session, tenant, user, generation or target is no longer the one the action began under. */
export class OwnerChangedError extends Error {
  constructor() {
    super('The session or the purchase order changed before the request was sent');
    this.name = 'OwnerChangedError';
  }
}

/** A request answer that did not describe the target it was asked for. */
export class UnexpectedResponseError extends Error {
  constructor() {
    super('The answer did not match the request');
    this.name = 'UnexpectedResponseError';
  }
}

export type Outcome<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: unknown };

/**
 * A request WAS sent, and its answer arrived after the session, tenant, user, generation, lock, a permission or the target had
 * changed. The answer must not reach the screen, the cache or a notification; it is carried only so the journal of the request's
 * own owner can learn what the server decided.
 */
export class LateResultError extends Error {
  constructor(readonly outcome: Outcome<unknown>) {
    super('The result arrived after the session changed');
    this.name = 'LateResultError';
  }
}

/** A refusal of the client that happened before anything was sent. */
export const isLocalRefusal = (error: unknown): boolean => error instanceof ReceivingAccessError || error instanceof OwnerChangedError;
/** Errors a screen must swallow silently: they concern an owner or target that is no longer on screen. */
export const isSessionCancel = (error: unknown): boolean => error instanceof OwnerChangedError || error instanceof LateResultError;

// ---------------------------------------------------------------------------
// Fences
// ---------------------------------------------------------------------------

/** The target (purchase order) of a screen, with a generation that moves on every change, so A, B, A is not A. */
export interface TargetBinding {
  readonly target: string | null;
  readonly generation: number;
}

export interface Fence {
  readonly owner: ReceivingOwner;
  readonly epoch: number;
  readonly target: string | null;
  /** True while nothing that mattered changed since the fence was taken (sticky: a change and its reversal still breaks it). */
  holds: () => boolean;
  /**
   * True while the owner, generation, lock and the three permissions are the ones the fence was taken under (sticky, like `holds`),
   * whatever became of the screen. It is the condition for cache reconciliation and nothing else: it never makes a result current.
   */
  authHolds: () => boolean;
  /** Stops watching the store. Call exactly once when the answer is back. */
  release: () => void;
}

/** What a fence compares: owner, generation, lock and the three permissions. */
function stateKey(): string {
  const state = useAuthStore.getState();
  const live = readReceivingAccess(state);
  if (live.owner === null || !live.active) return 'inactive';
  return JSON.stringify([ownerId(live.owner), live.epoch, live.canRead, live.canReceive, live.canFinancial]);
}

/**
 * The fence of NOW for an action that began under `expected` (the access of the render that offered it) on `binding`. Throws
 * before anything is sent when that owner or generation is not the live one, when the target moved, or when a needed permission is
 * no longer held.
 */
export function takeFence(
  expected: Pick<ReceivingAccess, 'owner' | 'epoch'>,
  permissions: readonly string[],
  binding: { render: TargetBinding; live: () => TargetBinding },
): Fence {
  if (expected.owner === null) throw new ReceivingAccessError(['session']);
  const state = useAuthStore.getState();
  const live = readReceivingAccess(state);
  if (!live.active || live.owner === null || !ownerEquals(live.owner, expected.owner) || live.epoch !== expected.epoch) {
    throw new OwnerChangedError();
  }
  const missing = permissions.filter((permission) => !state.hasPermission(permission));
  if (missing.length > 0) throw new ReceivingAccessError(missing);
  const now = binding.live();
  if (now.target !== binding.render.target || now.generation !== binding.render.generation) throw new OwnerChangedError();

  const startKey = stateKey();
  const startGeneration = now.generation;
  const target = binding.render.target;
  let broken = false;
  const unsubscribe = useAuthStore.subscribe(() => {
    if (!broken && stateKey() !== startKey) broken = true;
  });
  return {
    owner: live.owner,
    epoch: live.epoch,
    target,
    holds: () => {
      if (broken || stateKey() !== startKey) return false;
      const current = binding.live();
      return current.target === target && current.generation === startGeneration;
    },
    authHolds: () => !broken && stateKey() === startKey,
    release: unsubscribe,
  };
}
